/**
 * [A8] SubscriptionHealthBanner
 *
 * Full-width top banner for three abnormal subscription states.
 * Sits below OfflineBanner in the layout stack.
 *
 * halted            — red   — card charge failed; user must act
 * pending           — amber — Razorpay auto-retrying; no user action
 * cancelled_scheduled — amber — cancellation queued; period still running
 *
 * All three are dismissable per session (sessionStorage).
 *
 * Figma: [A8] SubscriptionHealthBanner
 */
import React, { useState } from "react";

export type HealthBannerVariant = "halted" | "pending" | "cancelled_scheduled";

interface Props {
    variant: HealthBannerVariant;
    planLabel: string;
    periodEndDate?: string | null;
    nextRetryDate?: string | null;  // future: populated when backend stores charge_at
    onAction: () => void;
}

const DISMISS_KEYS: Record<HealthBannerVariant, string> = {
    halted:               "sub_health_halted_dismissed",
    pending:              "sub_health_pending_dismissed",
    cancelled_scheduled:  "sub_health_cancelled_dismissed",
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
    });
}

export const SubscriptionHealthBanner: React.FC<Props> = ({
    variant,
    planLabel,
    periodEndDate,
    nextRetryDate,
    onAction,
}) => {
    const [dismissed, setDismissed] = useState(
        () => !!sessionStorage.getItem(DISMISS_KEYS[variant]),
    );

    if (dismissed) return null;

    const dismiss = () => {
        sessionStorage.setItem(DISMISS_KEYS[variant], "1");
        setDismissed(true);
    };

    const isRed = variant === "halted";

    const message = (() => {
        if (variant === "halted") {
            return `Subscription paused — update payment method to restore ${planLabel} features.`;
        }
        if (variant === "pending") {
            const dateStr = nextRetryDate ? formatDate(nextRetryDate) : "soon";
            return `Last payment failed — Razorpay will retry on ${dateStr}. No action needed.`;
        }
        const dateStr = periodEndDate ? formatDate(periodEndDate) : "soon";
        return `${planLabel} ends on ${dateStr}. Reactivate to keep your features.`;
    })();

    const actionLabel = (() => {
        if (variant === "halted") return "Update payment method";
        if (variant === "pending") return "Manage";
        return "Reactivate";
    })();

    return (
        <div
            className={`w-full flex items-center justify-between px-4 sm:px-6 py-2.5 gap-4 ${
                isRed
                    ? "bg-red-600"
                    : "bg-amber-50 border-b border-amber-200"
            }`}
        >
            <div className="flex items-center gap-2 min-w-0">
                {variant === "pending" ? (
                    <svg className={`w-4 h-4 flex-shrink-0 ${isRed ? "text-white" : "text-amber-600"}`}
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10" />
                        <polyline points="1 20 1 14 7 14" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                ) : variant === "cancelled_scheduled" ? (
                    <svg className={`w-4 h-4 flex-shrink-0 ${isRed ? "text-white" : "text-amber-600"}`}
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
                    </svg>
                ) : (
                    <svg className="w-4 h-4 flex-shrink-0 text-white"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                )}
                <span className={`text-sm font-medium line-clamp-2 ${isRed ? "text-white" : "text-amber-800"}`}>
                    {message}
                </span>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                <button
                    onClick={onAction}
                    className={`text-xs font-semibold px-3 py-2 min-h-[36px] inline-flex items-center rounded-lg transition-colors ${
                        isRed
                            ? "bg-white text-red-700 hover:bg-red-50"
                            : "bg-amber-700 text-white hover:bg-amber-800"
                    }`}
                >
                    {actionLabel}
                </button>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className={`p-2.5 rounded-md hover:bg-black/10 ${isRed ? "text-white" : "text-amber-700"}`}
                >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default SubscriptionHealthBanner;
